import { useState, useEffect, useMemo } from 'react';
import { Command } from 'cmdk';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuth } from '@/components/AuthProvider';
import { canAccessRoute } from '@logisticslash/shared';
import { SHELL } from '@/constants/testIds';

const NAV_ITEMS = [
  { href: '/', label: 'Overview', icon: 'LayoutDashboard' },
  { href: '/loads', label: 'Loads', icon: 'Package' },
  { href: '/dispatch', label: 'Dispatch', icon: 'Columns3' },
  { href: '/trips', label: 'Trips', icon: 'Route' },
  { href: '/tracking', label: 'Tracking', icon: 'MapPinned' },
  { href: '/parties', label: 'Parties', icon: 'Users' },
  { href: '/fleet', label: 'Fleet', icon: 'Truck' },
  { href: '/drivers', label: 'Drivers', icon: 'UserRound' },
  { href: '/settlements', label: 'Settlements', icon: 'Wallet' },
  { href: '/invoices', label: 'Invoices', icon: 'FileText' },
  { href: '/ar', label: 'AR / Aging', icon: 'Landmark' },
  { href: '/ifta', label: 'IFTA', icon: 'Fuel' },
  { href: '/calculator', label: 'Calculator', icon: 'Calculator' },
  { href: '/ai', label: 'Rate Con Import', icon: 'Sparkles' },
  { href: '/import', label: 'Import', icon: 'Upload' },
  { href: '/qb', label: 'QuickBooks', icon: 'BookOpen' },
  { href: '/docs', label: 'Documents', icon: 'FolderOpen' },
  { href: '/guides', label: 'Guides', icon: 'Library' },
  { href: '/settings', label: 'Settings', icon: 'Settings' },
];

function isPlausibleLoadNumber(value) {
  const v = String(value ?? '').trim();
  if (v.length < 3 || v.length > 64) return false;
  if (/^(test|asdf|qwer|xxx|null|undefined|n\/a)$/i.test(v)) return false;
  if (/^[a-z]{6,}$/i.test(v) && !/\d/.test(v)) return false;
  if (/^[0-9a-f]{8,}$/i.test(v) && !/[-_/]/.test(v)) return false;
  return /[A-Za-z0-9]/.test(v);
}

export function CommandBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [proposal, setProposal] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;

  const navItems = useMemo(
    () => NAV_ITEMS.filter((item) => (role ? canAccessRoute(role, item.href) : false)),
    [role]
  );

  const loadsQ = useQuery({
    queryKey: ['loads'],
    enabled: open,
    queryFn: async () => {
      const r = await api('/api/v1/loads');
      if (r.status !== 200) throw new Error(r.data?.error || 'loads');
      return r.data.loads ?? [];
    },
  });

  const loads = useMemo(() => {
    const rows = loadsQ.data ?? [];
    return rows.filter((l) => isPlausibleLoadNumber(l.loadNumber));
  }, [loadsQ.data]);

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    const handler = () => setOpen(true);
    document.addEventListener('keydown', down);
    window.addEventListener('ls:open-command', handler);
    return () => {
      document.removeEventListener('keydown', down);
      window.removeEventListener('ls:open-command', handler);
    };
  }, []);

  const handleSelect = (href) => {
    navigate(href);
    setOpen(false);
    setQuery('');
  };

  async function runAi() {
    if (!query.trim() || aiBusy) return;
    setAiBusy(true);
    try {
      const r = await api('/api/v1/command/parse', {
        method: 'POST',
        body: { text: query },
        timeoutMs: 60000,
      });
      if (r.status !== 200) {
        toast.error(String(r.data?.error ?? 'Command failed'));
        return;
      }
      const p = r.data.proposal;
      setProposal(p);
      if (p?.kind === 'navigate' && p.href) {
        toast.message(p.summary || 'Opening…');
        handleSelect(p.href);
        return;
      }
      if (p?.kind === 'read') {
        toast.message(p.summary);
        setOpen(false);
      } else {
        setConfirmOpen(true);
      }
    } catch (err) {
      toast.error(err?.message ?? 'Command failed');
    } finally {
      setAiBusy(false);
    }
  }

  async function confirmMutation() {
    if (!proposal?.endpoint) return;
    const r = await api(proposal.endpoint.path, {
      method: proposal.endpoint.method,
      body: proposal.endpoint.body,
    });
    if (r.status >= 200 && r.status < 300) {
      toast.success('Action completed');
      setOpen(false);
      setQuery('');
      setProposal(null);
      if (r.data?.load?.id) navigate(`/loads?highlight=${encodeURIComponent(r.data.load.id)}`);
    } else {
      toast.error(String(r.data?.error ?? 'Action failed'));
    }
  }

  return (
    <>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in" />
          <Dialog.Content
            data-testid={SHELL.commandInput}
            className="fixed left-1/2 top-[20%] -translate-x-1/2 w-full max-w-xl bg-card border rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95"
          >
            <Command shouldFilter={true}>
              <div className="flex items-center gap-2 border-b px-4 py-3">
                <Icons.Search className="h-4 w-4 text-muted-foreground" />
                <Command.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Jump to page, load, or ask AI..."
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || !query.trim()) return;
                    // Cmd/Ctrl/Shift+Enter always asks AI; plain Enter asks AI for multi-word commands.
                    const forceAi =
                      e.metaKey || e.ctrlKey || e.shiftKey || /\s/.test(query.trim());
                    if (forceAi) {
                      e.preventDefault();
                      e.stopPropagation();
                      void runAi();
                    }
                  }}
                />
                <kbd className="text-xs bg-muted px-1.5 py-0.5 rounded border">ESC</kbd>
              </div>
              <Command.List className="max-h-[400px] overflow-y-auto p-2">
                <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
                  No matches. Press Enter on Ask AI, or Run AI.
                </Command.Empty>

                <Command.Group
                  heading="Navigate"
                  className="text-xs text-muted-foreground px-2 py-1.5"
                >
                  {navItems.map((item) => {
                    const Icon = Icons[item.icon] || Icons.Circle;
                    return (
                      <Command.Item
                        key={item.href}
                        value={item.label}
                        onSelect={() => handleSelect(item.href)}
                        className="flex items-center gap-2 px-2 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-accent text-foreground"
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Command.Item>
                    );
                  })}
                </Command.Group>

                <Command.Group heading="Loads" className="text-xs text-muted-foreground px-2 py-1.5">
                  {loads.slice(0, 8).map((l) => (
                    <Command.Item
                      key={l.id}
                      value={`load ${l.loadNumber}`}
                      onSelect={() => {
                        handleSelect(`/loads?highlight=${encodeURIComponent(l.id)}`);
                        toast.message(`Load ${l.loadNumber}`);
                      }}
                      className="flex items-center gap-2 px-2 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-accent text-foreground"
                    >
                      <Icons.Package className="h-4 w-4" />
                      <span className="font-mono">{l.loadNumber}</span>
                    </Command.Item>
                  ))}
                </Command.Group>

                <Command.Group heading="AI" className="text-xs text-muted-foreground px-2 py-1.5">
                  <Command.Item
                    value={`ask ai run command ${query}`}
                    onSelect={() => void runAi()}
                    className="flex items-center gap-2 px-2 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-accent text-foreground"
                  >
                    <Icons.Sparkles className="h-4 w-4 text-primary" />
                    Ask AI: <span className="font-medium">{query.trim() || '…'}</span>
                    {aiBusy ? <span className="text-xs text-muted-foreground ml-2">…</span> : null}
                  </Command.Item>
                </Command.Group>
              </Command.List>
            </Command>
            <div className="flex items-center gap-2 px-4 py-3 border-t">
              <ButtonRunAi onClick={() => void runAi()} disabled={aiBusy || !query.trim()} />
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setOpen(false)}
              >
                Close
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Confirm AI action?"
        description={proposal?.summary ?? 'Execute proposed mutation via secured API.'}
        confirmLabel="Confirm"
        onConfirm={confirmMutation}
      />
    </>
  );
}

function ButtonRunAi({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground h-8 px-3 hover:bg-primary/90 disabled:opacity-50"
    >
      Run AI
    </button>
  );
}
